import React, { useState, useEffect, useRef } from "react";
import { FileImage } from "lucide-react";
import { Detection } from "@/types";
import { cn } from "@/utils/cn";

/**
 * Safely parse a timestamp string into a formatted date/time
 * Handles various formats including ISO strings and Python datetime strings
 */
const formatTimestamp = (timestamp: string | null | undefined): { date: string; time: string } => {
  if (!timestamp) {
    return { date: "-", time: "" };
  }

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      // Try parsing as Python datetime format (YYYY-MM-DD HH:MM:SS.ffffff)
      const pythonDateMatch = timestamp.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
      if (pythonDateMatch) {
        const parsed = new Date(`${pythonDateMatch[1]}T${pythonDateMatch[2]}Z`);
        if (!isNaN(parsed.getTime())) {
          return {
            date: parsed.toLocaleDateString(),
            time: parsed.toLocaleTimeString(),
          };
        }
      }
      return { date: "-", time: "" };
    }
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  } catch {
    return { date: "-", time: "" };
  }
};

export interface AnalysisThumbnailProps {
  analysisId: number;
  imageName: string;
  totalDetections: number;
  timestamp: string;
  detections?: Detection[];
  loadImage: (id: number) => Promise<string | null>;
  onDelete?: (analysisId: number) => void;
  isProcessing?: boolean;
  uploadData?: {
    fileId: string;
    file: File;
    preview: string;
  };
  /** Aspect ratio class - defaults to "aspect-square" */
  aspectRatio?: "aspect-square" | "aspect-video" | "aspect-[16/9]";
  /** Show colored border based on detection state - defaults to true */
  showBorder?: boolean;
}

/**
 * Thumbnail component for analyzed images with lazy loading,
 * detection count badges, and state-based border colors
 */
export const AnalysisThumbnail: React.FC<AnalysisThumbnailProps> = ({
  analysisId,
  imageName,
  totalDetections,
  timestamp,
  detections,
  loadImage,
  onDelete,
  isProcessing = false,
  uploadData,
  aspectRatio = "aspect-square",
  showBorder = true,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const thumbnailRef = useRef<HTMLDivElement>(null);

  const getDetectionCounts = () => {
    if (!detections) {
      return { certain: 0, uncertain: 0, corrected: 0, userDrawn: 0 };
    }

    let certain = 0;
    let uncertain = 0;
    let corrected = 0;
    let userDrawn = 0;

    detections.forEach((detection) => {
      if (detection.user_drawn) {
        userDrawn++;
      } else if (detection.is_user_corrected && !detection.user_drawn) {
        corrected++;
      } else if (
        !detection.user_drawn &&
        detection.is_confident &&
        detection.passes_cutoff !== false &&
        detection.passes_refiner !== false
      ) {
        certain++;
      } else {
        uncertain++;
      }
    });

    return { certain, uncertain, corrected, userDrawn };
  };

  const counts = getDetectionCounts();
  const hasNoDetections = totalDetections === 0;
  const hasUnhandledUncertainDetections = counts.uncertain > 0;
  const imageStateFrameColor =
    hasNoDetections || hasUnhandledUncertainDetections
      ? "border-orange-400"
      : "border-green-400";

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      {
        root: null,
        rootMargin: "50px",
        threshold: 0.1,
      },
    );

    if (thumbnailRef.current) {
      observer.observe(thumbnailRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isVisible]);

  // Load image when component becomes visible
  useEffect(() => {
    if (!isVisible || imageUrl) return;

    if (isProcessing && uploadData?.preview) {
      setImageUrl(uploadData.preview);
      setLoading(false);
      return;
    }

    if (analysisId < 0 || isProcessing) {
      return;
    }

    let isMounted = true;

    const loadImageAsync = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const url = await loadImage(analysisId);
        if (isMounted) {
          setImageUrl(url);
        }
      } catch (err) {
        console.error(
          `Failed to load thumbnail for analysis ${analysisId}:`,
          err,
        );
        if (isMounted) {
          setError("Failed to load");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImageAsync();

    return () => {
      isMounted = false;
    };
  }, [isVisible, analysisId, loadImage, imageUrl, isProcessing, uploadData]);

  return (
    <>
      <div
        ref={thumbnailRef}
        className={cn(
          "relative bg-gray-100 rounded overflow-hidden group",
          aspectRatio,
          showBorder && "border-2",
          showBorder && imageStateFrameColor,
        )}
      >
        {!isVisible ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <FileImage className="h-8 w-8 text-gray-300" />
          </div>
        ) : loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : error || !imageUrl ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <FileImage className="h-8 w-8 text-gray-400" />
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={imageName}
            className="w-full h-full object-cover"
          />
        )}

        {isProcessing && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-3 text-center shadow-lg">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-xs text-gray-700 font-medium">Processing...</p>
            </div>
          </div>
        )}

        {onDelete && !isProcessing && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(analysisId);
            }}
            className="absolute top-2 left-2 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center z-10 transition-all opacity-0 group-hover:opacity-100"
          >
            <span className="text-xs">×</span>
          </button>
        )}
      </div>

      <div className="mt-2">
        <div className="text-xs flex items-center">
          <span className="text-black">{totalDetections}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-green-600">{counts.certain}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-orange-600">{counts.uncertain}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-purple-600">{counts.corrected}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-blue-600">{counts.userDrawn}</span>
        </div>

        <div className="text-xs text-gray-500 mt-1 flex items-center">
          <span>
            {formatTimestamp(timestamp).date}{" "}
            {formatTimestamp(timestamp).time}
          </span>
        </div>
      </div>
    </>
  );
};
